import {
  resolveWearablePathOverride,
  type WearablePathOverrideEntry,
} from "./wearables.pathOverrideMemory";
import { getAdaptiveRecommendation } from "./wearablesAdaptiveEngine";
import { buildWearablesRecommendation } from "./wearablesRecommendationEngine";
import type { WearablesContext } from "./wearablesContextEngine";
import type {
  WearablePathDefaults,
  WearablePathTrack,
} from "./wearables.pathDefaults";

export type WearablesRouteSurface = "guided-studio" | "omnistudio" | "xr";

export type WearablesContinuityStatus =
  | "suggested"
  | "accepted"
  | "dismissed"
  | "launched"
  | "resumed";

export interface WearablesContinuityEntry {
  surface: WearablesRouteSurface;
  href: string;
  label: string;
  recommendedTrack: WearablePathTrack;
  effectiveTrack: WearablePathTrack;
  status: WearablesContinuityStatus;
  title: string;
  body: string;
  chips: string[];
  updatedAt: string;
}

export type WearablesContinuityMemory = Partial<
  Record<WearablesRouteSurface, WearablesContinuityEntry | null>
>;

type WearablesRouteSurfaceMeta = {
  surface: WearablesRouteSurface;
  label: string;
  href: string;
};

export type WearablesRouteCompanionModel = WearablesRouteSurfaceMeta & {
  recommendedTrack: WearablePathTrack;
  effectiveTrack: WearablePathTrack;
  signalTrack: WearablePathTrack;
  destinationSurface: WearablesRouteSurface;
  destinationHref: string;
  destinationLabel: string;
  title: string;
  body: string;
  sourceLabel: string;
  stateLabel: string;
  continuityChips: string[];
  inlineChips: string[];
  adaptiveReason: "signal" | "habit" | "trajectory" | "hybrid";
  confidence: number;
  context: WearablesContext;
  fromWearables: boolean;
  shouldInterrupt: boolean;
  status: WearablesContinuityStatus | null;
};

const ADAPTIVE_CANDIDATE_MODES: WearablePathTrack[] = [
  "creator",
  "xr",
  "readiness",
  "recovery",
  "general",
];

const SURFACE_META: Record<WearablesRouteSurface, WearablesRouteSurfaceMeta> = {
  "guided-studio": {
    surface: "guided-studio",
    label: "Guided Studio",
    href: "/guided-studio",
  },
  omnistudio: {
    surface: "omnistudio",
    label: "OmniStudio",
    href: "/omnistudio",
  },
  xr: {
    surface: "xr",
    label: "XR",
    href: "/guided/start-from-world",
  },
};

function titleCaseTrack(track: WearablePathTrack): string {
  switch (track) {
    case "xr":
      return "XR";
    case "general":
    default:
      return track.charAt(0).toUpperCase() + track.slice(1);
  }
}

function buildSurfaceCopy(surface: WearablesRouteSurface, track: WearablePathTrack): string {
  const trackLabel = titleCaseTrack(track);

  switch (surface) {
    case "guided-studio":
      return `${trackLabel} guidance is ready before Guided Studio opens its full builder flow.`;
    case "omnistudio":
      return `${trackLabel} ordering is ready before OmniStudio expands into its full editorial surface.`;
    case "xr":
      return `${trackLabel} continuity is ready before XR world-start expands into spatial setup.`;
    default:
      return `${trackLabel} continuity is ready.`;
  }
}

export function resolveWearablesRouteSurface(
  pathname: string,
): WearablesRouteSurfaceMeta | null {
  if (pathname === "/guided-studio") {
    return SURFACE_META["guided-studio"];
  }

  if (pathname === "/omnistudio") {
    return SURFACE_META.omnistudio;
  }

  if (pathname === "/guided/start-from-world" || pathname === "/xr") {
    return SURFACE_META.xr;
  }

  return null;
}

export function resolveWearablesRouteHref(surface: WearablesRouteSurface): string {
  return SURFACE_META[surface].href;
}

export function buildWearablesRouteCompanionModel(args: {
  pathname: string;
  search: string;
  baseDefaults: WearablePathDefaults;
  overrideEntry?: WearablePathOverrideEntry | null;
  continuityEntry?: WearablesContinuityEntry | null;
}): WearablesRouteCompanionModel | null {
  const surfaceMeta = resolveWearablesRouteSurface(args.pathname);

  if (!surfaceMeta) {
    return null;
  }

  const searchParams = new URLSearchParams(args.search);
  const fromWearables = searchParams.get("entry") === "wearables";
  const resolved = resolveWearablePathOverride(args.baseDefaults, args.overrideEntry);
  const effectiveDefaults = resolved.effectiveDefaults;
  const baseRecommendation = buildWearablesRecommendation({
    pathname: args.pathname,
    search: args.search,
    baseDefaults: args.baseDefaults,
    overrideEntry: args.overrideEntry,
  });

  if (!baseRecommendation) {
    return null;
  }

  const signalTrack = baseRecommendation.mode;
  const adaptiveRecommendation = getAdaptiveRecommendation({
    mode: signalTrack,
    label: baseRecommendation.label,
    destination: baseRecommendation.destination,
    surface: surfaceMeta.surface,
    baseConfidence: baseRecommendation.confidence,
    continuityEntry: args.continuityEntry,
    candidateModes:
      resolved.overrideMode === "pinned"
        ? [signalTrack]
        : [signalTrack, ...baseRecommendation.alternatives],
    candidateDestinations: ["guided-studio", "omnistudio", "xr"],
  });
  const effectiveTrack = adaptiveRecommendation.mode;

  let title = effectiveDefaults.guidedStudio.entryEmphasis.title;
  let body = effectiveDefaults.guidedStudio.entryEmphasis.body;

  if (surfaceMeta.surface === "omnistudio") {
    title = effectiveDefaults.omniStudio.entryEmphasis.title;
    body = effectiveDefaults.omniStudio.entryEmphasis.body;
  }

  if (surfaceMeta.surface === "xr") {
    title = `Start XR with ${titleCaseTrack(effectiveTrack)} continuity`;
    body = buildSurfaceCopy(surfaceMeta.surface, effectiveTrack);
  }

  title = adaptiveRecommendation.narrative.title;
  body = adaptiveRecommendation.narrative.body;

  const confidenceLabel = adaptiveRecommendation.confidence >= 0.8
    ? "Strong fit"
    : adaptiveRecommendation.confidence >= 0.68
      ? "Adaptive fit"
      : "Signal fit";

  const reasonLabel =
    adaptiveRecommendation.reason === "trajectory"
      ? "Flow aligned"
      : adaptiveRecommendation.reason === "habit"
        ? "Creator ready"
        : adaptiveRecommendation.reason === "hybrid"
          ? "Flow ready"
          : "Ready now";

  const continuityChips = [
    adaptiveRecommendation.narrative.inlineLabel,
    confidenceLabel,
    resolved.stateLabel,
    surfaceMeta.label,
  ];

  return {
    ...surfaceMeta,
    recommendedTrack: resolved.baseTrack,
    effectiveTrack,
    signalTrack,
    destinationSurface: adaptiveRecommendation.destinationSurface,
    destinationHref: adaptiveRecommendation.destination,
    destinationLabel: adaptiveRecommendation.destinationLabel,
    title,
    body,
    sourceLabel:
      adaptiveRecommendation.reason === "signal"
        ? effectiveDefaults.advisory.sourceLabel
        : `Signal, session context, and lightweight continuity memory are keeping ${reasonLabel.toLowerCase()} without hard-locking the route.`,
    stateLabel: resolved.stateLabel,
    continuityChips,
    inlineChips: [effectiveDefaults.advisory.sourceLabel, ...continuityChips].slice(0, 3),
    adaptiveReason: adaptiveRecommendation.reason,
    confidence: adaptiveRecommendation.confidence,
    context: adaptiveRecommendation.context,
    fromWearables,
    shouldInterrupt:
      !fromWearables &&
      resolved.overrideMode !== "dismissed" &&
      args.continuityEntry?.status !== "dismissed",
    status: args.continuityEntry?.status ?? null,
  };
}

export function buildWearablesContinuityEntry(
  model: WearablesRouteCompanionModel,
  status: WearablesContinuityStatus,
): WearablesContinuityEntry {
  return {
    surface: model.surface,
    href: model.destinationHref,
    label: model.destinationLabel,
    recommendedTrack: model.recommendedTrack,
    effectiveTrack: model.effectiveTrack,
    status,
    title: model.title,
    body: model.body,
    chips: model.continuityChips,
    updatedAt: new Date().toISOString(),
  };
}